import { LanguageKey } from '../utils/language';
import { getSnowplowCollectorUrlOrDefault } from '../utils/snowplow';

export enum AppEnv {
  Production = 'production',
  ProductionUsa = 'productionUsa',
  Staging = 'staging',
  Development = 'development',
  Dynamic = 'dynamic',
  Local = 'local',
}

export function isProductionEnv(appenv: AppEnv): boolean {
  return [AppEnv.Production, AppEnv.ProductionUsa].includes(appenv);
}

export const ROOT_HOST = 'genially.com';
export const ROOT_CANNY = 'geniallyfeedback';
const DYNAMIC_ROOT_HOST = 'genial.ly';

export type AppEnvLocationOrDynamicBranchName =
  | { location: string }
  | { dynamicBranchName: string | undefined };

function getDynamicAppHost(
  locationOrDynamicBranchName: AppEnvLocationOrDynamicBranchName,
) {
  const placeholderDynamicHost = `$placeholder$-dynamic.${DYNAMIC_ROOT_HOST}`;

  if ('dynamicBranchName' in locationOrDynamicBranchName) {
    const { dynamicBranchName } = locationOrDynamicBranchName;

    if (dynamicBranchName) {
      let newDynamicBranchName = dynamicBranchName.replace(/_/g, '-');

      let substringIndex = 40;

      while (newDynamicBranchName[substringIndex - 1] === '-') {
        substringIndex -= 1;
      }

      newDynamicBranchName = newDynamicBranchName.slice(0, substringIndex);

      return `${newDynamicBranchName}-dynamic.${DYNAMIC_ROOT_HOST}`;
    }

    return placeholderDynamicHost;
  }

  const { location } = locationOrDynamicBranchName;

  const { host } = new URL(location);

  if (!host.endsWith(DYNAMIC_ROOT_HOST)) {
    return placeholderDynamicHost;
  }

  const hostParts = host.split('.');
  return hostParts[0].endsWith('-dynamic') ? host : placeholderDynamicHost;
}

export type LocalizedData = Record<LanguageKey, string>;

export interface AppUrls {
  readonly web: string;
  readonly panel: string;
  readonly panel404: string;
  readonly editor: string;
  readonly view: string;
  readonly view404: string;
  readonly viewPrint: string;
  readonly preview: string;
  readonly newApi: string;
  readonly migrationsApi: string;
  readonly wsServer: string;
  readonly auth: string;
  readonly authUS?: string;
  readonly paymentsApi: string;
  readonly pptImporter: string;
  readonly pdfImporter: string;
  readonly gslidesImporter: string;
  readonly editorLog: string;
  readonly panelLog: string;
  readonly viewLog: string;
  readonly loggerServer: string | undefined;
  readonly metricsServer: string | undefined;
  readonly elearningDownloader: string;
  readonly elearningApi: string;
  readonly tracking: string;
  readonly play: string;
  readonly p2pServer: string;
  readonly analytics: string;
  readonly resources: string;
  readonly slsUtils: string;
  readonly screenshotterUrl: string;
  readonly newScreenshotterUrl: string;
  readonly downloadGeniallyAsHTML: string;
  readonly compressLambda: string;
  readonly blog: LocalizedData;
  readonly academy: LocalizedData;
  readonly systemStatus: LocalizedData;
  readonly helpCenterLinks: LocalizedData;
  readonly analyticsSupportLinks: LocalizedData;
  readonly userTrackingSupportLinks: LocalizedData;
  readonly interactiveQuestionsSupportLinks: LocalizedData;
  readonly ltiSupportLinks: LocalizedData;
  readonly storageSpaceSupportLinks: LocalizedData;
  readonly playSupportLinks: LocalizedData;
  readonly cannyFeedbackLinks: LocalizedData;
  readonly typeform: string;
  readonly saveBackupLambda?: string;
  readonly snowplowCollectorUrl: string;
  readonly lti: string;
  readonly otherValidFrontendUrls: readonly string[];
  readonly geniallyCommunityApiUrl: string;
  readonly canvaIntegration: string;
}

export enum TypeformId {
  CancelPlan = 'cancelPlan',
  CancelTeamPlan = 'cancelTeamPlan',
  DeleteAccount = 'deleteAccount',
  Suggestions = 'suggestions',
  UpgradeMyPlan = 'upgradeMyPlan',
}

export interface AppKeys {
  readonly cannyAppId: string;
  readonly embedlyApiKey: string;
  readonly facebookAppId: number;
  readonly firebase: {
    readonly apiKey: string;
    readonly authDomain: string;
    readonly databaseURL: string;
    readonly storageBucket: string;
    readonly messagingSenderId: string;
  };
  readonly froalaApiKey: string;
  readonly googleAnalyticsId: string;
  readonly iframelyApiKey: string;
  readonly typeformIds: Record<TypeformId, LocalizedData>;
}

export interface AppEnvConfig {
  readonly production: boolean;
  readonly dynamic: boolean;
  readonly urls: AppUrls;
  readonly keys: AppKeys;
}

function isEnvActive(location: string, envConfig: AppEnvConfig): boolean {
  // if we are in one of these hosts we consider the env active
  const validFrontendUrls = [
    envConfig.urls.panel,
    envConfig.urls.editor,
    envConfig.urls.view,
    envConfig.urls.viewPrint,
    envConfig.urls.preview,
    envConfig.urls.auth,
    envConfig.urls.play,
    ...envConfig.urls.otherValidFrontendUrls,
  ].map(
    // strip tail '/'
    url => (url.endsWith('/') ? url.slice(0, url.length - 1) : url),
  );

  return validFrontendUrls.some(
    validUrl => location === validUrl || location.startsWith(`${validUrl}/`),
  );
}

export function getStaticEnvConfigs(): Record<
  | AppEnv.Production
  | AppEnv.ProductionUsa
  | AppEnv.Staging
  | AppEnv.Development
  | AppEnv.Local,
  AppEnvConfig
> {
  const production: AppEnvConfig = {
    production: true,
    dynamic: false,
    keys: {
      cannyAppId: '66012a60afe89426bb4b2012',
      firebase: {
        apiKey: 'AIzaSyDgJvCkBoF2ELh4I2cAQ9JGsxQwmG2h_yY',
        authDomain: 'genially-d1c40.firebaseapp.com',
        databaseURL: 'https://genially-d1c40.firebaseio.com',
        storageBucket: 'genially-d1c40.appspot.com',
        messagingSenderId: '554227012837',
      },
      facebookAppId: 1494586530820799,
      googleAnalyticsId: 'UA-56678436-1', // This GA is to give data to our users
      embedlyApiKey: 'eac46e7ba2af4800adf4210c29a9bcb6',
      iframelyApiKey: 'd94a0eb55704680be3cd47',
      froalaApiKey:
        '2J1B10dA5D4A5A3E3A3H3C-22VKOG1FGULVKHXDXNDXc2a1Kd1SNdF3H3A8A6D4F4B4E3C2A16==',
      typeformIds: {
        cancelPlan: {
          [LanguageKey.Spanish]: 'nKK51rdT',
          [LanguageKey.English]: 'VRhre4',
          [LanguageKey.French]: 'gGPtna',
          [LanguageKey.Brazilian]: 'VRhre4',
          [LanguageKey.Italian]: 'VRhre4',
          [LanguageKey.German]: 'VRhre4', // TODO: DE change
        },
        cancelTeamPlan: {
          [LanguageKey.Spanish]: 'nKK51rdT', // TODO: Only spanish for now
          [LanguageKey.English]: 'VRhre4',
          [LanguageKey.French]: 'gGPtna',
          [LanguageKey.Brazilian]: 'VRhre4',
          [LanguageKey.Italian]: 'VRhre4',
          [LanguageKey.German]: 'VRhre4', // TODO: DE change
        },
        deleteAccount: {
          [LanguageKey.Spanish]: 'HhOlmC',
          [LanguageKey.English]: 'Klitp3',
          [LanguageKey.French]: 'nGfdUM',
          [LanguageKey.Brazilian]: 'fweUZE9a',
          [LanguageKey.Italian]: 'Klitp3',
          [LanguageKey.German]: 'Klitp3', // TODO: DE change
        },
        suggestions: {
          [LanguageKey.Spanish]: 'L7H7XhOa',
          [LanguageKey.English]: 'nv6KZRvZ',
          [LanguageKey.French]: 'H6s3QBQC',
          [LanguageKey.Brazilian]: 'AeaLfdhC',
          [LanguageKey.Italian]: 'nv6KZRvZ',
          [LanguageKey.German]: 'nv6KZRvZ', // TODO: DE change
        },
        upgradeMyPlan: {
          [LanguageKey.Spanish]: 'fxLApIHl',
          [LanguageKey.English]: 'o6zGy8M7',
          [LanguageKey.French]: 'FYnMfRZC',
          [LanguageKey.Brazilian]: 'nhtuGO59',
          [LanguageKey.Italian]: 'mFDTBwHY',
          [LanguageKey.German]: 'jRZknMB0',
        },
      },
    },
    urls: {
      web: `https://${ROOT_HOST}`,
      panel: `https://app.${ROOT_HOST}`,
      get panel404() {
        return `${this.panel}/404`;
      },
      editor: `https://app.${ROOT_HOST}/editor/`,
      view: `https://view.${ROOT_HOST}`,
      get view404() {
        return `${this.view}/404`;
      },
      viewPrint: 'https://print.genial.ly',
      preview: `https://view.${ROOT_HOST}/Preview/Index/`,
      newApi: `https://api.${ROOT_HOST}`,
      migrationsApi: `https://migrations.${ROOT_HOST}`,
      wsServer: `https://ws.${ROOT_HOST}`,
      auth: `https://auth.${ROOT_HOST}`,
      paymentsApi: `https://payments.${ROOT_HOST}`,
      pptImporter: `https://ppt.${ROOT_HOST}/import/ppt`,
      pdfImporter: `https://ppt.${ROOT_HOST}/import/pdf`,
      gslidesImporter: `https://ppt.${ROOT_HOST}/import/gslides`,
      editorLog: `https://q04tggcme9.execute-api.us-east-1.amazonaws.com/prod/log`,
      panelLog: `https://q04tggcme9.execute-api.us-east-1.amazonaws.com/prod/log`,
      viewLog: `https://33rsqia5l9.execute-api.us-east-1.amazonaws.com/prod/log`,
      loggerServer: `https://logger.${ROOT_HOST}`,
      metricsServer: `https://amen.${ROOT_HOST}/metrics`,
      elearningDownloader: `https://elearning-download.${ROOT_HOST}/download`,
      elearningApi: `https://elearning-download.${ROOT_HOST}/api`,
      tracking: `https://tracking.${ROOT_HOST}`,
      play: `https://play.${ROOT_HOST}`,
      p2pServer: `https://p2p.${ROOT_HOST}`,
      analytics: `https://analytics.${ROOT_HOST}`,
      resources: `https://content.${ROOT_HOST}/api/v1`,
      slsUtils: `https://t806zzjvhj.execute-api.eu-west-1.amazonaws.com/prod`,
      screenshotterUrl: `https://amq3ezc8t0.execute-api.eu-west-1.amazonaws.com/prod/screenshots/pdf`,
      newScreenshotterUrl: `https://xaxyqmobm7.execute-api.eu-west-1.amazonaws.com/production/invocations`,
      downloadGeniallyAsHTML: `https://genially-download-html.${ROOT_HOST}/v2/download-html`,
      compressLambda:
        'https://qw03mugydg.execute-api.eu-west-1.amazonaws.com/prod/compress',
      saveBackupLambda: `https://mcf5p4j5zj.execute-api.eu-west-1.amazonaws.com/pro/signed-url`,
      geniallyCommunityApiUrl: `https://genially-searcher.${ROOT_HOST}`,
      canvaIntegration: 'https://genially.com/canva/',

      get blog() {
        const base = `https://blog.${ROOT_HOST}`;

        return {
          [LanguageKey.Spanish]: `${base}/?lang=es`,
          [LanguageKey.English]: `${base}/?lang=en`,
          [LanguageKey.French]: `${base}/?lang=fr`,
          [LanguageKey.Brazilian]: `${base}/?lang=en`, // TODO: pt-br change
          [LanguageKey.Italian]: `${base}/?lang=en`, // TODO: IT change
          [LanguageKey.German]: `${base}/?lang=en`, // TODO: DE change
        };
      },

      get academy() {
        const base = `https://academy.${ROOT_HOST}`;

        return {
          [LanguageKey.Spanish]: `${base}/es/`,
          [LanguageKey.English]: `${base}/en/`,
          [LanguageKey.French]: `${base}/fr/`,
          [LanguageKey.Brazilian]: `${base}/en/`, // TODO: pt-br change
          [LanguageKey.Italian]: `${base}/en/`, // TODO: IT change
          [LanguageKey.German]: `${base}/en/`, // TODO: DE change
        };
      },

      get systemStatus() {
        const base = `https://status.${ROOT_HOST}`;

        return {
          [LanguageKey.Spanish]: base,
          [LanguageKey.English]: base,
          [LanguageKey.French]: base,
          [LanguageKey.Brazilian]: base,
          [LanguageKey.Italian]: base,
          [LanguageKey.German]: base,
        };
      },

      get helpCenterLinks() {
        const base = `https://help.${ROOT_HOST}`;

        return {
          [LanguageKey.Spanish]: `${base}/lang/es/?from=profile_menu`,
          [LanguageKey.English]: `${base}/lang/en_us/?from=profile_menu`,
          [LanguageKey.French]: `${base}/lang/fr/?from=profile_menu`,
          [LanguageKey.Brazilian]: `${base}/lang/pt_br/?from=profile_menu`,
          [LanguageKey.Italian]: `${base}/lang/it/?from=profile_menu`,
          [LanguageKey.German]: `${base}/lang/de/?from=profile_menu`,
        };
      },

      get analyticsSupportLinks() {
        const base = `https://help.${ROOT_HOST}`;

        return {
          [LanguageKey.Spanish]: `${base}/es/como-obtener-metricas-de-tus-geniallys-r1WhswHno?from=statistics_genially`,
          [LanguageKey.English]: `${base}/en_us/how-to-get-statistics-on-your-geniallys-r1WhswHno?from=statistics_genially`,
          [LanguageKey.French]: `${base}/fr/collectez-les-statistiques-de-vos-geniallys-r1WhswHno?from=statistics_genially`,
          [LanguageKey.Brazilian]: `${base}/pt_br/como-obter-estatisticas-dos-seus-geniallys-r1WhswHno?from=statistics_genially`,
          [LanguageKey.Italian]: `${base}/it/come-ottenere-le-statistiche-dei-tuoi-genially-r1WhswHno?from=statistics_genially`,
          [LanguageKey.German]: `${base}/de/wie-du-metriken-deiner-geniallys-erhaltst-r1WhswHno?from=statistics_genially`,
        };
      },

      get userTrackingSupportLinks() {
        const base = `https://help.${ROOT_HOST}`;

        return {
          [LanguageKey.Spanish]: `${base}/es/como-obtener-metricas-y-seguimiento-de-usuarios-en-genially-HJnsiwB3j?from=seguimiento`,
          [LanguageKey.English]: `${base}/en_us/how-to-get-metrics-and-activate-user-tracking-in-genially-HJnsiwB3j?from=seguimiento`,
          [LanguageKey.French]: `${base}/fr/comment-obtenir-des-mtriques-et-suivre-lexprience-des-utilisateurs-HJnsiwB3j?from=seguimiento`,
          [LanguageKey.Brazilian]: `${base}/pt_br/como-obter-metricas-e-ativar-o-monitoramento-de-usurios-em-genially-HJnsiwB3j?from=seguimiento`,
          [LanguageKey.Italian]: `${base}/it/come-ottenere-le-metriche-e-monitorare-gli-utenti-su-genially-HJnsiwB3j?from=seguimiento`,
          [LanguageKey.German]: `${base}/de/so-erhaltst-du-die-metriken-und-nachverfolgung-des-nutzerverhaltens-in-genially-HJnsiwB3j?from=seguimiento`,
        };
      },

      get interactiveQuestionsSupportLinks() {
        const base = `https://help.${ROOT_HOST}`;

        return {
          [LanguageKey.Spanish]: `${base}/es/preguntas-interactivas-en-genially-H1_tCj1en?from=sidebar_question_settings`,
          [LanguageKey.English]: `${base}/en_us/interactive-questions-in-genially-H1_tCj1en?from=sidebar_question_settings`,
          [LanguageKey.French]: `${base}/fr/les-questions-interactives-de-genially-H1_tCj1en?from=sidebar_question_settings`,
          [LanguageKey.Brazilian]: `${base}/pt_br/perguntas-interativas-em-genially-H1_tCj1en?from=sidebar_question_settings`,
          [LanguageKey.Italian]: `${base}/it/domande-interattive-su-genially-H1_tCj1en?from=sidebar_question_settings`,
          [LanguageKey.German]: `${base}/de/interaktive-fragen-bei-genially-H1_tCj1en?from=sidebar_question_settings`,
        };
      },

      get ltiSupportLinks() {
        const base = `https://help.${ROOT_HOST}`;

        return {
          [LanguageKey.Spanish]: `${base}/es/cmo-integrar-genially-a-tu-lms-mediante-lti-B1ZFC45Na`,
          [LanguageKey.English]: `${base}/en_us/how-to-integrate-genially-into-your-lms-using-lti-B1ZFC45Na`,
          [LanguageKey.French]: `${base}/fr/comment-intgrer-genially-dans-votre-lms-laide-de-lti-B1ZFC45Na`,
          [LanguageKey.Brazilian]: `${base}/pt_br/como-integrar-genially-ao-seu-lms-usando-lti-B1ZFC45Na`,
          [LanguageKey.Italian]: `${base}/it/come-integrare-genially-con-il-tuo-lms-tramite-lti-B1ZFC45Na`,
          [LanguageKey.German]: `${base}/de/wie-du-genially-ber-lti-in-dein-lms-integrierst-B1ZFC45Na`,
        };
      },

      get cannyFeedbackLinks() {
        const base = `https://${ROOT_CANNY}.canny.io`;

        return {
          [LanguageKey.Spanish]: `${base}/peticion-de-nuevas-funcionalidades`,
          [LanguageKey.English]: `${base}/peticion-de-nuevas-funcionalidades`,
          [LanguageKey.French]: `${base}/peticion-de-nuevas-funcionalidades`,
          [LanguageKey.Brazilian]: `${base}/peticion-de-nuevas-funcionalidades`,
          [LanguageKey.Italian]: `${base}/peticion-de-nuevas-funcionalidades`,
          [LanguageKey.German]: `${base}/peticion-de-nuevas-funcionalidades`,
        };
      },

      get storageSpaceSupportLinks() {
        const base = `https://help.${ROOT_HOST}`;

        return {
          [LanguageKey.Spanish]: `${base}/es/espacio-de-almacenamiento-en-genially-B1jfJom40`,
          [LanguageKey.English]: `${base}/en_us/storage-space-in-genially-B1jfJom40`,
          [LanguageKey.French]: `${base}/fr/espace-de-stockage-sur-genially-B1jfJom40`,
          [LanguageKey.Brazilian]: `${base}/pt_br/espaco-de-armazenamento-na-genially-B1jfJom40`,
          [LanguageKey.Italian]: `${base}/it/spazio-di-archiviazione-su-genially-B1jfJom40`,
          [LanguageKey.German]: `${base}/de/speicherplatz-auf-genially-B1jfJom40`,
        };
      },

      get playSupportLinks() {
        const base = `https://help.${ROOT_HOST}`;

        return {
          [LanguageKey.Spanish]: `${base}/es/presentar-genially-Hy6i99yuke`,
          [LanguageKey.English]: `${base}/en_us/present-genially-Hy6i99yuke`,
          [LanguageKey.French]: `${base}/fr/presenter-genially-Hy6i99yuke`,
          [LanguageKey.Brazilian]: `${base}/it/presentare-genially-Hy6i99yuke`,
          [LanguageKey.Italian]: `${base}/pt_br/apresentar-genially-Hy6i99yuke`,
          [LanguageKey.German]: `${base}/de/genially-prasentiert-Hy6i99yuke`,
        };
      },

      typeform: 'https://geniallyiscool.typeform.com/to',
      snowplowCollectorUrl: getSnowplowCollectorUrlOrDefault(`https://sp.${ROOT_HOST}`),
      lti: `https://lti-api.${ROOT_HOST}`,
      otherValidFrontendUrls: [],
    },
  };

  const productionUsa: AppEnvConfig = {
    production: true,
    dynamic: false,
    keys: production.keys,
    urls: {
      ...production.urls,
      panel: `https://app-usa.${ROOT_HOST}`,
      editor: `https://app-usa.${ROOT_HOST}/editor/`,
      newApi: `https://api-usa.${ROOT_HOST}`,
      auth: `https://auth-usa.${ROOT_HOST}`,
    },
  };

  const staging: AppEnvConfig = {
    production: false,
    dynamic: false,
    keys: {
      cannyAppId: production.keys.cannyAppId,
      firebase: production.keys.firebase,
      facebookAppId: production.keys.facebookAppId,
      googleAnalyticsId: production.keys.googleAnalyticsId,
      embedlyApiKey: production.keys.embedlyApiKey,
      iframelyApiKey: production.keys.iframelyApiKey,
      froalaApiKey: production.keys.froalaApiKey,
      typeformIds: production.keys.typeformIds,
    },
    urls: {
      web: production.urls.web,
      panel: `https://app-staging.${ROOT_HOST}`,
      get panel404() {
        return `${this.panel}/404`;
      },
      editor: `https://app-staging.${ROOT_HOST}/editor/`,
      view: `https://preview.${ROOT_HOST}`,
      get view404() {
        return `${this.view}/404`;
      },
      viewPrint: `https://preview.${ROOT_HOST}`,
      preview: `https://preview.${ROOT_HOST}/Preview/Index/`,
      newApi: `https://api-staging.${ROOT_HOST}`,
      migrationsApi: `https://api-staging.${ROOT_HOST}`,
      wsServer: `https://ws-pre.${ROOT_HOST}`,
      auth: `https://auth-staging.${ROOT_HOST}`,
      paymentsApi: `https://payments-staging.${ROOT_HOST}`,
      pptImporter: production.urls.pptImporter,
      pdfImporter: production.urls.pdfImporter,
      gslidesImporter: production.urls.gslidesImporter,
      editorLog: production.urls.editorLog,
      panelLog: production.urls.panelLog,
      viewLog: production.urls.viewLog,
      loggerServer: `https://logger-pre.genially.com`,
      metricsServer: `https://amen.${ROOT_HOST}/metrics`,
      elearningDownloader: `https://elearning-download-staging.${ROOT_HOST}/download`,
      elearningApi: `https://elearning-download-staging.${ROOT_HOST}/api`,
      tracking: `https://tracking-pre.${ROOT_HOST}`,
      play: `https://play-staging.${ROOT_HOST}`,
      p2pServer: `https://p2p-staging.${ROOT_HOST}`,
      analytics: `https://analytics-pre.${ROOT_HOST}`,
      resources: `https://content-staging.${ROOT_HOST}/api/v1`,
      slsUtils: `https://1x0i8zw55j.execute-api.eu-west-1.amazonaws.com/staging`,
      screenshotterUrl: `https://oz780iqafg.execute-api.eu-west-1.amazonaws.com/staging/screenshots/pdf`,
      newScreenshotterUrl: `https://62tflwgk9b.execute-api.eu-west-1.amazonaws.com/staging/invocations`,
      downloadGeniallyAsHTML: `https://genially-download-html-staging.${ROOT_HOST}/v2/download-html`,
      compressLambda: production.urls.compressLambda,
      blog: production.urls.blog,
      academy: production.urls.academy,
      systemStatus: production.urls.systemStatus,
      helpCenterLinks: production.urls.helpCenterLinks,
      analyticsSupportLinks: production.urls.analyticsSupportLinks,
      userTrackingSupportLinks: production.urls.userTrackingSupportLinks,
      interactiveQuestionsSupportLinks: production.urls.interactiveQuestionsSupportLinks,
      ltiSupportLinks: production.urls.ltiSupportLinks,
      storageSpaceSupportLinks: production.urls.storageSpaceSupportLinks,
      playSupportLinks: production.urls.playSupportLinks,
      cannyFeedbackLinks: production.urls.cannyFeedbackLinks,
      typeform: production.urls.typeform,
      saveBackupLambda: production.urls.saveBackupLambda,
      snowplowCollectorUrl: getSnowplowCollectorUrlOrDefault(''),
      lti: `https://lti-api-pre.${ROOT_HOST}`,
      otherValidFrontendUrls: [],
      geniallyCommunityApiUrl: `https://genially-searcher-staging.${ROOT_HOST}`,
      canvaIntegration: 'https://genially.com/canva/',
    },
  };

  const development: AppEnvConfig = {
    production: false,
    dynamic: false,
    keys: {
      cannyAppId: production.keys.cannyAppId,
      firebase: production.keys.firebase,
      facebookAppId: production.keys.facebookAppId,
      googleAnalyticsId: production.keys.googleAnalyticsId,
      embedlyApiKey: production.keys.embedlyApiKey,
      iframelyApiKey: production.keys.iframelyApiKey,
      froalaApiKey: production.keys.froalaApiKey,
      typeformIds: production.keys.typeformIds,
    },
    urls: {
      web: production.urls.web,
      panel: `https://app-dev.${ROOT_HOST}`,
      get panel404() {
        return `${this.panel}/404`;
      },
      editor: `https://app-dev.${ROOT_HOST}/editor/`,
      view: `https://devview.${ROOT_HOST}`,
      get view404() {
        return `${this.view}/404`;
      },
      viewPrint: `https://devview.${ROOT_HOST}`,
      preview: `https://devview.${ROOT_HOST}/Preview/Index/`,
      newApi: `https://api-dev.${ROOT_HOST}`,
      migrationsApi: `https://api-dev.${ROOT_HOST}`,
      wsServer: `https://ws-dev.${ROOT_HOST}`,
      auth: `https://auth-dev.${ROOT_HOST}`,
      paymentsApi: `https://payments-dev.${ROOT_HOST}`,
      pptImporter: production.urls.pptImporter,
      pdfImporter: production.urls.pdfImporter,
      gslidesImporter: production.urls.gslidesImporter,
      editorLog: production.urls.editorLog,
      panelLog: production.urls.panelLog,
      viewLog: production.urls.viewLog,
      loggerServer: `https://logger-pre.genially.com`,
      metricsServer: `https://amen.${ROOT_HOST}/metrics`,
      elearningDownloader: staging.urls.elearningDownloader,
      elearningApi: staging.urls.elearningApi,
      tracking: staging.urls.tracking,
      play: `https://play-dev.${ROOT_HOST}`,
      p2pServer: `https://p2p-dev.${ROOT_HOST}`,
      analytics: staging.urls.analytics,
      resources: `https://content-dev.${ROOT_HOST}/api/v1`,
      slsUtils: 'https://gvv3gujs2j.execute-api.eu-west-2.amazonaws.com/dev',
      screenshotterUrl: staging.urls.screenshotterUrl,
      newScreenshotterUrl: staging.urls.newScreenshotterUrl,
      downloadGeniallyAsHTML: `https://genially-download-html-dev.${ROOT_HOST}/v2/download-html`,
      compressLambda:
        'https://xfsujxby91.execute-api.us-east-1.amazonaws.com/dev/compress',
      blog: production.urls.blog,
      academy: production.urls.academy,
      systemStatus: production.urls.systemStatus,
      helpCenterLinks: production.urls.helpCenterLinks,
      analyticsSupportLinks: production.urls.analyticsSupportLinks,
      userTrackingSupportLinks: production.urls.userTrackingSupportLinks,
      interactiveQuestionsSupportLinks: production.urls.interactiveQuestionsSupportLinks,
      ltiSupportLinks: production.urls.ltiSupportLinks,
      storageSpaceSupportLinks: production.urls.storageSpaceSupportLinks,
      playSupportLinks: production.urls.playSupportLinks,
      cannyFeedbackLinks: production.urls.cannyFeedbackLinks,
      typeform: production.urls.typeform,
      saveBackupLambda: production.urls.saveBackupLambda,
      snowplowCollectorUrl: getSnowplowCollectorUrlOrDefault(''),
      lti: staging.urls.lti,
      otherValidFrontendUrls: [],
      geniallyCommunityApiUrl: `https://genially-searcher-staging.${ROOT_HOST}`,
      canvaIntegration: 'https://genially.com/canva/',
    },
  };

  const local: AppEnvConfig = {
    production: false,
    dynamic: false,
    keys: {
      cannyAppId: production.keys.cannyAppId,
      firebase: production.keys.firebase,
      facebookAppId: production.keys.facebookAppId,
      googleAnalyticsId: production.keys.googleAnalyticsId,
      embedlyApiKey: production.keys.embedlyApiKey,
      iframelyApiKey: production.keys.iframelyApiKey,
      froalaApiKey: production.keys.froalaApiKey,
      typeformIds: production.keys.typeformIds,
    },
    urls: {
      web: production.urls.web,
      panel: `http://localhost:3000`,
      get panel404() {
        return `${this.panel}/404`;
      },
      editor: `http://localhost:4242/`,
      view: 'http://localhost:8181',
      get view404() {
        return `${this.view}/404`;
      },
      viewPrint: 'http://host.docker.internal:8181',
      preview: 'http://localhost:8181/Preview/Index/',
      newApi: `http://localhost:3030`,
      migrationsApi: `http://localhost:3030`,
      wsServer: `http://localhost:5444`,
      auth: 'http://localhost:5001',
      paymentsApi: `http://localhost:8001`,
      pptImporter: `http://localhost:8687/import/ppt`,
      pdfImporter: `http://localhost:8687/import/pdf`,
      gslidesImporter: `http://localhost:8687/import/gslides`,
      editorLog: production.urls.editorLog,
      panelLog: production.urls.panelLog,
      viewLog: production.urls.viewLog,
      loggerServer: undefined,
      metricsServer: undefined,
      elearningDownloader: `http://localhost:1209`,
      elearningApi: `http://localhost:1209`,
      tracking: `http://localhost:1210`,
      play: `http://localhost:3928`,
      p2pServer: `http://localhost:5928`,
      analytics: `http://localhost:8081`,
      resources: `http://localhost:9091/api/v1`,
      slsUtils: development.urls.slsUtils,
      screenshotterUrl: 'http://localhost:8787',
      // Review doc from the new screenshotter app for testing locally
      newScreenshotterUrl: 'http://localhost:8787',
      downloadGeniallyAsHTML: 'http://localhost:1987/v2/download-html',
      compressLambda: development.urls.compressLambda,
      blog: production.urls.blog,
      academy: production.urls.academy,
      systemStatus: production.urls.systemStatus,
      helpCenterLinks: production.urls.helpCenterLinks,
      analyticsSupportLinks: production.urls.analyticsSupportLinks,
      userTrackingSupportLinks: production.urls.userTrackingSupportLinks,
      interactiveQuestionsSupportLinks: production.urls.interactiveQuestionsSupportLinks,
      ltiSupportLinks: production.urls.ltiSupportLinks,
      storageSpaceSupportLinks: production.urls.storageSpaceSupportLinks,
      playSupportLinks: production.urls.playSupportLinks,
      cannyFeedbackLinks: production.urls.cannyFeedbackLinks,
      typeform: production.urls.typeform,
      saveBackupLambda: `http://localhost:3000/pro/signed-url`,
      snowplowCollectorUrl: getSnowplowCollectorUrlOrDefault('http://localhost:9090'),
      lti: 'http://localhost:3001',
      otherValidFrontendUrls: [
        'http://localhost:3006', // embedding test
      ],
      geniallyCommunityApiUrl: `http://localhost:3014`,
      canvaIntegration: 'https://genially.com/canva/',
    },
  };

  return {
    [AppEnv.Production]: production,
    [AppEnv.ProductionUsa]: productionUsa,
    [AppEnv.Staging]: staging,
    [AppEnv.Development]: development,
    [AppEnv.Local]: local,
  };
}

export function getAllEnvConfigs(
  locationOrDynamicBranchName: AppEnvLocationOrDynamicBranchName,
): Record<AppEnv, AppEnvConfig> {
  const staticEnvConfigs = getStaticEnvConfigs();
  const production = staticEnvConfigs[AppEnv.Production];
  const staging = staticEnvConfigs[AppEnv.Staging];

  const dynamicHost = getDynamicAppHost(locationOrDynamicBranchName);

  const dynamic: AppEnvConfig = {
    production: false,
    dynamic: true,
    keys: {
      cannyAppId: production.keys.cannyAppId,
      firebase: production.keys.firebase,
      facebookAppId: production.keys.facebookAppId,
      googleAnalyticsId: production.keys.googleAnalyticsId,
      embedlyApiKey: production.keys.embedlyApiKey,
      iframelyApiKey: production.keys.iframelyApiKey,
      froalaApiKey: production.keys.froalaApiKey,
      typeformIds: production.keys.typeformIds,
    },
    urls: {
      web: production.urls.web,
      panel: `https://${dynamicHost}`,
      get panel404() {
        return `${this.panel}/404`;
      },
      editor: `https://${dynamicHost}/editor/`,
      view: `https://view-${dynamicHost}`,
      get view404() {
        return `${this.view}/404`;
      },
      viewPrint: `https://view-${dynamicHost}`,
      preview: `https://view-${dynamicHost}/Preview/Index/`,
      newApi: `https://api-${dynamicHost}`,
      migrationsApi: `https://api-${dynamicHost}`,
      wsServer: `https://ws-${dynamicHost}`,
      auth: `https://auth-${dynamicHost}`,
      authUS: `https://auth-us-${dynamicHost}`,
      paymentsApi: `https://pay-${dynamicHost}`,
      pptImporter: `https://ppt-${dynamicHost}/import/ppt`,
      pdfImporter: `https://ppt-${dynamicHost}/import/pdf`,
      gslidesImporter: `https://ppt-${dynamicHost}/import/gslides`,
      editorLog: production.urls.editorLog,
      panelLog: production.urls.panelLog,
      viewLog: production.urls.viewLog,
      loggerServer: `https://logger-pre.genially.com`,
      metricsServer: undefined,
      elearningDownloader: `https://elearn-${dynamicHost}/download`,
      elearningApi: `https://elearn-${dynamicHost}/api`,
      tracking: `https://track-${dynamicHost}`,
      play: `https://play-${dynamicHost}`,
      p2pServer: `https://p2p-${dynamicHost}`,
      analytics: `https://anal-${dynamicHost}`,
      resources: `https://content-${dynamicHost}/api/v1`,
      slsUtils: staging.urls.slsUtils,
      screenshotterUrl: staging.urls.screenshotterUrl,
      newScreenshotterUrl: staging.urls.newScreenshotterUrl,
      downloadGeniallyAsHTML: production.urls.downloadGeniallyAsHTML,
      compressLambda: production.urls.compressLambda,
      blog: production.urls.blog,
      academy: production.urls.academy,
      systemStatus: production.urls.systemStatus,
      helpCenterLinks: production.urls.helpCenterLinks,
      analyticsSupportLinks: production.urls.analyticsSupportLinks,
      userTrackingSupportLinks: production.urls.userTrackingSupportLinks,
      interactiveQuestionsSupportLinks: production.urls.interactiveQuestionsSupportLinks,
      ltiSupportLinks: production.urls.ltiSupportLinks,
      storageSpaceSupportLinks: production.urls.storageSpaceSupportLinks,
      playSupportLinks: production.urls.playSupportLinks,
      cannyFeedbackLinks: production.urls.cannyFeedbackLinks,
      typeform: production.urls.typeform,
      snowplowCollectorUrl: getSnowplowCollectorUrlOrDefault(
        `https://snow-${dynamicHost}`,
      ),
      lti: `https://lti-api-${dynamicHost}`,
      otherValidFrontendUrls: [],
      geniallyCommunityApiUrl: `https://genially-searcher-staging.${ROOT_HOST}`,
      canvaIntegration: 'https://genially.com/canva/',
    },
  };

  return {
    ...staticEnvConfigs,
    [AppEnv.Dynamic]: dynamic,
  };
}

export function getCurrentAppEnv(location: string): AppEnv {
  const entry = Object.entries(getAllEnvConfigs({ location })).find(env => {
    return isEnvActive(location, env[1]);
  });

  if (!entry) {
    // unknown, assume production
    return AppEnv.Production;
  }

  return entry[0] as AppEnv;
}

export function getCurrentAppEnvConfig(
  location: string,
  dynamicBranchName?: string,
): AppEnvConfig {
  const env = getCurrentAppEnv(location);
  const allEnvsData = getAllEnvConfigs(
    dynamicBranchName ? { dynamicBranchName } : { location },
  );
  return allEnvsData[env];
}
